import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroResponsable from './components/HeroResponsable';
import SelfTestSection from './components/SelfTestSection';
import SignsSection from './components/SignsSection';
import ResourcesSection from './components/ResourcesSection';

const chapters = [
  { id: 'senales', label: 'Señales de alerta', icon: 'ri-alarm-warning-line' },
  { id: 'test', label: 'Test de autoevaluación', icon: 'ri-questionnaire-line' },
  { id: 'recursos', label: 'Recursos de ayuda', icon: 'ri-hospital-line' },
];

const BASE = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${BASE}/juego-responsable`,
      url: `${BASE}/juego-responsable`,
      name: 'Juego Responsable | Ayuda y Recursos FEJAR | Apuestamente',
      description: 'Recursos de ayuda para el juego responsable en España. Test de autoevaluación, señales de alerta y contacto con FEJAR (900 200 225).',
      inLanguage: 'es-ES',
      isPartOf: { '@id': `${BASE}/#website` },
    },
    {
      '@type': 'FAQPage',
      '@id': `${BASE}/juego-responsable#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: '¿Qué es el juego responsable?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'El juego responsable implica apostar dentro de tus posibilidades económicas, establecer límites de tiempo y dinero, y reconocer cuándo el juego deja de ser entretenimiento para convertirse en un problema.',
          },
        },
        {
          '@type': 'Question',
          name: '¿Cuál es el teléfono de ayuda para la ludopatía en España?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'El teléfono de FEJAR (Federación Española de Jugadores de Azar Rehabilitados) es el 900 200 225, gratuito y disponible todos los días.',
          },
        },
        {
          '@type': 'Question',
          name: '¿Cómo puedo autoexcluirme de las apuestas en España?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Puedes solicitar la autoexclusión a través del Registro General de Interdicciones de Acceso al Juego (RGIAJ) de la DGOJ, o directamente en cada operador de juego online regulado en España.',
          },
        },
      ],
    },
  ],
};

export default function JuegoResponsablePage() {
  useEffect(() => {
    document.title = 'Juego Responsable | Ayuda y Recursos FEJAR | Apuestamente';
    const id = 'schema-juego-responsable';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(SCHEMA);
    return () => { document.getElementById(id)?.remove(); };
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#080a0f]">
      <Navbar />

      <HeroResponsable />

      {/* Sticky chapter nav */}
      <div className="sticky top-16 z-40 bg-[#0d0f14]/95 backdrop-blur-md border-b border-[#1e2028]">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex items-center gap-1 overflow-x-auto py-2">
            {chapters.map((c) => (
              <button
                key={c.id}
                onClick={() => scrollTo(c.id)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-[#6b7280] hover:text-white hover:bg-white/5 transition-all whitespace-nowrap cursor-pointer flex-shrink-0"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${c.icon} text-xs`}></i>
                </div>
                {c.label}
              </button>
            ))}
            <a
              href="tel:900200225"
              className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg bg-[#ef4444]/10 border border-[#ef4444]/20 text-[#ef4444] text-sm font-semibold hover:bg-[#ef4444]/20 transition-all whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-xs"></i>
              </div>
              FEJAR · 900 200 225
            </a>
          </div>
        </div>
      </div>

      <main>
        <SignsSection />
        <SelfTestSection />
        <ResourcesSection />
      </main>

      {/* Footer disclaimer */}
      <section className="py-10 bg-[#080a0f] border-t border-[#1e2028]">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-5 bg-[#0d0f14] border border-[#1e2028] rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#ef4444]/10 flex-shrink-0">
                <i className="ri-user-forbid-line text-[#ef4444] text-lg"></i>
              </div>
              <p className="text-[#6b7280] text-xs leading-relaxed max-w-2xl">
                <strong className="text-[#a8b2c1]">+18 · Solo para adultos.</strong> Las apuestas deportivas están prohibidas para menores de 18 años. El juego puede crear adicción. Si tienes un problema, llama al <strong className="text-white">900 200 225</strong> (FEJAR). Apuestamente promueve el juego responsable y cumple con la normativa DGOJ.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-2 h-2 rounded-full bg-[#00e87a]"></div>
              <span className="text-[#6b7280] text-xs whitespace-nowrap">Licencias DGOJ</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
